import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import MapPanel from '../components/workspace/Map/MapPanel';
import MapLayerList from '../components/workspace/Map/MapLayerList';
import { embedApi } from '../api/embedApi';
import { clearStoredPass, readStoredPass, writeStoredPass } from '../components/embed/captchaPass';
import { useTurnstile } from '../components/embed/useTurnstile';
import { getDefaultPalette } from '../utils/colorPalettes';
import { MAP_NO_METRIC } from '../utils/mapLayerMetricUtils';
import {
  computeStatsFromGeoJsonProperty,
  getGeoJSONFitBounds,
  h3ToGeoJSON,
  isHighCardinalityCategorical,
  itemsToGeoJSON,
  layerHasColorableProperty,
} from '../utils/mapUtils';

const MapBrand = () => (
  <a
    href="https://manteo.ai"
    target="_blank"
    rel="noopener noreferrer"
    className="fixed top-3 right-3 z-[70] flex items-center gap-1.5 rounded-full border border-gray-200 bg-white/95 px-2.5 py-1 shadow-sm"
  >
    <span className="text-[11px] text-gray-500">Powered by</span>
    <img src="/logo.svg" alt="manteo.ai" className="h-4 w-auto" />
  </a>
);

const geojsonFromItems = (items) => {
  if (!items?.length) return { type: 'FeatureCollection', features: [] };
  if (items[0].geometry) return itemsToGeoJSON(items);
  if (items[0].h3) return h3ToGeoJSON(items);
  return { type: 'FeatureCollection', features: [] };
};

const EmbedMapPage = () => {
  const [searchParams] = useSearchParams();
  const publicKey = searchParams.get('key') || '';
  const mapId = searchParams.get('map') || '';
  const mapRef = useRef(null);
  const [layer, setLayer] = useState(null);
  const [error, setError] = useState('');
  const [captcha, setCaptcha] = useState(null);
  const [needsChallenge, setNeedsChallenge] = useState(false);
  const [hoverInfo, setHoverInfo] = useState(null);
  const [selectedInfo, setSelectedInfo] = useState(null);
  const passRef = useRef(readStoredPass(publicKey));
  const siteKey = needsChallenge ? (captcha?.site_key || '') : '';
  const { containerRef, acquireToken, error: captchaError } = useTurnstile(siteKey);

  const showMap = useCallback((data) => {
    if (data.captcha_pass) {
      passRef.current = data.captcha_pass;
      writeStoredPass(publicKey, data.captcha_pass);
    }
    const geojson = geojsonFromItems(data.items);
    setLayer({
      id: mapId,
      name: data.name || 'Map',
      visible: true,
      hue: 152,
      sat: 72,
      data: geojson,
      stats: data.stats || { min: 0, max: 1, is_categorical: false },
      palette: getDefaultPalette(),
      config: {
        palette: getDefaultPalette(),
        inverted: false,
        colorProperty: 'value',
        uniformColor: false,
      },
      status: 'loaded',
      query: data.query_spec || null,
    });
  }, [mapId, publicKey]);

  useEffect(() => {
    let cancelled = false;
    embedApi.getCaptcha()
      .then(data => {
        if (!cancelled) setCaptcha(data);
      })
      .catch(() => {
        if (!cancelled) setError('Captcha is unavailable.');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!publicKey || !mapId) {
      setError('This map link is incomplete.');
      return undefined;
    }
    if (!captcha) return undefined;
    const pass = passRef.current;
    if (captcha.required && !pass) {
      if (!captcha.site_key) setError('Captcha is not configured.');
      else setNeedsChallenge(true);
      return undefined;
    }
    let cancelled = false;
    embedApi.getMap(publicKey, mapId, { captchaPass: pass })
      .then(data => {
        if (!cancelled) showMap(data);
      })
      .catch(err => {
        if (cancelled) return;
        if (captcha.required && err.status === 403 && captcha.site_key) {
          passRef.current = '';
          clearStoredPass(publicKey);
          setNeedsChallenge(true);
          return;
        }
        setError(err.message || 'This map is unavailable.');
      });
    return () => {
      cancelled = true;
    };
  }, [publicKey, mapId, captcha, showMap]);

  useEffect(() => {
    if (!needsChallenge) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const token = await acquireToken();
        if (cancelled) return;
        const data = await embedApi.getMap(publicKey, mapId, { captchaToken: token });
        if (!cancelled) {
          setNeedsChallenge(false);
          showMap(data);
        }
      } catch (err) {
        if (!cancelled) setError(err.message || 'This map is unavailable.');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [needsChallenge, acquireToken, publicKey, mapId, showMap]);

  const updateLayer = (layerId, recipe) => {
    setLayer(current => (current && current.id === layerId ? recipe(current) : current));
  };

  const fitMap = (map) => {
    const bounds = layer?.data && getGeoJSONFitBounds(layer.data);
    if (bounds) map.fitBounds(bounds, { padding: 64, maxZoom: 10, duration: 0 });
  };

  if (error) {
    return (
      <div className="flex h-full items-center justify-center bg-white text-sm text-gray-600">
        {error}
        <MapBrand />
      </div>
    );
  }

  if (!layer) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3 bg-white px-6 text-sm text-gray-500">
        <p>{siteKey ? 'Confirm you are human to open this map.' : 'Loading map…'}</p>
        {siteKey && <div ref={containerRef} />}
        {captchaError && <p className="text-red-600">{captchaError}</p>}
        <MapBrand />
      </div>
    );
  }

  return (
    <div className="fixed inset-0 overflow-hidden bg-white">
      <MapPanel
        mapRef={mapRef}
        mapLayers={[layer]}
        hoverInfo={hoverInfo}
        selectedInfo={selectedInfo}
        onMouseMove={(event) => {
          const feature = event.features?.[0];
          setHoverInfo(feature ? { feature, lngLat: event.lngLat } : null);
        }}
        onMapClick={(event) => {
          const feature = event.features?.[0];
          setSelectedInfo(feature ? { feature, lngLat: event.lngLat } : null);
        }}
        onMapLoad={fitMap}
      />
      <MapLayerList
        readOnly
        layers={[layer]}
        onToggleVisibility={(layerId) => updateLayer(layerId, current => ({
          ...current,
          visible: !current.visible,
        }))}
        onRemoveLayer={() => {}}
        onUpdateTheme={(layerId, _color, palette = null, inverted = false) => updateLayer(layerId, current => {
          const nextPalette = palette || getDefaultPalette();
          return {
            ...current,
            palette: nextPalette,
            config: { ...current.config, palette: nextPalette, inverted },
          };
        })}
        onUpdateLayerClassification={(layerId, classification) => updateLayer(layerId, current => ({
          ...current,
          config: { ...current.config, ...classification },
          valueFilter: null,
        }))}
        onUpdateLayerMetric={() => {}}
        onUpdateLayerColorProperty={(layerId, propertyName) => updateLayer(layerId, current => {
          if (propertyName === MAP_NO_METRIC) {
            return {
              ...current,
              config: { ...current.config, uniformColor: true, colorProperty: null },
              stats: { min: 0, max: 1, is_categorical: false },
              valueFilter: null,
            };
          }
          if (!layerHasColorableProperty(current, propertyName)) return current;
          const stats = computeStatsFromGeoJsonProperty(current.data, propertyName);
          const highCardinality = isHighCardinalityCategorical(
            { ...current, stats, config: { ...current.config, colorProperty: propertyName } },
            propertyName
          );
          return {
            ...current,
            stats,
            valueFilter: null,
            config: {
              ...current.config,
              colorProperty: propertyName,
              palette: getDefaultPalette(),
              inverted: false,
              uniformColor: highCardinality,
              highCardinalityCategorical: highCardinality,
            },
            palette: getDefaultPalette(),
          };
        })}
        onPersistLayerStyle={() => {}}
        onAddLayer={() => {}}
        onPreviewLayer={() => {}}
        onOpenBuilder={() => {}}
        onFilterLayer={(layerId, valueRange) => updateLayer(layerId, current => {
          if (!valueRange) return { ...current, valueFilter: null };
          const same = current.valueFilter
            && current.valueFilter.min === valueRange.min
            && current.valueFilter.max === valueRange.max;
          return { ...current, valueFilter: same ? null : valueRange };
        })}
        onFlyToLayer={() => {
          const map = mapRef.current?.getMap?.() || mapRef.current;
          if (map) fitMap(map);
        }}
        onReorderLayers={() => {}}
      />
      <MapBrand />
    </div>
  );
};

export default EmbedMapPage;
